import {
  ID,
  PrecomputedCompletion,
  ScheduleEntryState,
  SortingMode,
  Task,
  TaskCheckOptions,
  TaskType,
  Timestamp,
} from '@griply/types';
import {
  formatDateKey,
  getScheduleByDate,
  getScheduleFrequencyDayTarget,
  getSortedTasks,
  getTaskDayCompletionPercentage,
  getWeekStartsOn,
  parseDateKey,
} from '@griply/utils';
import { startOfDay } from 'date-fns';
import { useMemo } from 'react';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  /**
   * Lower bound for completion occurrences. Habit and repeating-task
   * schedules can stretch back arbitrarily far, so without this the loop
   * over `schedule.entries` would build occurrences for the entire history.
   */
  fromDate?: Date;
};

export type CompletedDateTreeResult = {
  tree: Record<ID, TreeItem>;
  completionData: Record<string, PrecomputedCompletion>;
};

export const useCompletedDateTree = ({
  tasks,
  customOrder,
  sortingMode,
  fromDate,
}: Options): CompletedDateTreeResult => {
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  return useMemo(() => {
    const fromDayStart = fromDate ? startOfDay(fromDate) : undefined;

    const occurrences: {
      taskId: ID;
      dateKey: string;
      date: Timestamp;
      completedAt: Timestamp;
    }[] = [];

    tasks.forEach((task) => {
      // 1. Regular tasks / one-off completions
      if (task.completedAt) {
        const completedDay = startOfDay(task.completedAt);
        if (fromDayStart && completedDay < fromDayStart) {
          return;
        }
        occurrences.push({
          taskId: task.id,
          dateKey: formatDateKey(task.completedAt),
          date: completedDay,
          completedAt: task.completedAt,
        });
        return;
      }

      // 2. Habits and Repeating Tasks (from schedules)
      if (!task.schedules?.length) {
        return;
      }

      const completionsByDate = new Map<string, Timestamp>();

      // Check all entries for completions
      task.schedules.forEach((schedule) => {
        schedule.entries.forEach((entry) => {
          if (entry.state !== ScheduleEntryState.Complete || !entry.value) {
            return;
          }
          if (fromDayStart && startOfDay(entry.date) < fromDayStart) {
            return;
          }
          const dateKey = formatDateKey(entry.date);
          const prev = completionsByDate.get(dateKey);
          if (!prev || entry.date > prev) {
            completionsByDate.set(dateKey, entry.date);
          }
        });
      });

      completionsByDate.forEach((completedAt, dateKey) => {
        const date = startOfDay(parseDateKey(dateKey));
        occurrences.push({
          taskId: task.id,
          dateKey,
          date,
          completedAt,
        });
      });
    });

    const taskTypeMap = new Map<ID, TaskType>(tasks.map((t) => [t.id, t.type]));

    const dateSections: Record<
      string,
      { date: Timestamp; taskIds: ID[]; completionTimes: Map<ID, Timestamp> }
    > = {};
    occurrences.forEach((occ) => {
      if (!dateSections[occ.dateKey]) {
        dateSections[occ.dateKey] = {
          date: occ.date,
          taskIds: [],
          completionTimes: new Map(),
        };
      }
      if (!dateSections[occ.dateKey].taskIds.includes(occ.taskId)) {
        dateSections[occ.dateKey].taskIds.push(occ.taskId);
      }
      dateSections[occ.dateKey].completionTimes.set(
        occ.taskId,
        occ.completedAt,
      );
    });

    const dateTreeItems: Record<ID, TreeItem> = {};

    // Sort date sections chronologically reversed (latest first)
    const sortedDateKeys = Object.keys(dateSections).sort(
      (a, b) => dateSections[b].date.getTime() - dateSections[a].date.getTime(),
    );

    sortedDateKeys.forEach((dateKey) => {
      dateTreeItems[dateKey] = {
        id: dateKey,
        type: TreeItemTypes.Date,
        name: dateKey,
        children: [],
        isFolder: true,
      };
    });

    const customTaskOrder =
      sortingMode === SortingMode.Custom
        ? Object.keys(dateTreeItems).reduce<ID[]>((acc, sectionId) => {
            const sectionOrder = customOrder?.[sectionId] ?? [];
            acc.push(...sectionOrder);
            return acc;
          }, [])
        : [];

    // Precompute completion state per occurrence so the card can skip
    // expensive schedule-expansion hooks at render time.
    const completionData: Record<string, PrecomputedCompletion> = {};

    const allTreeItems = getSortedTasks(tasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: sortingMode ? [SortingMode.CreatedAt, sortingMode] : undefined,
    }).reduce<Record<ID, TreeItem>>((acc, task) => {
      Object.keys(dateSections).forEach((dateKey) => {
        if (dateSections[dateKey].taskIds.includes(task.id)) {
          const occurrenceId = `occurrence:${dateKey}:${task.id}`;
          if (acc[dateKey]) {
            acc[dateKey].children?.push(occurrenceId);
          }
          acc[occurrenceId] = {
            id: occurrenceId,
            name: task.name,
            type: TreeItemTypes.Task,
          };

          // Precompute completion meta for this occurrence
          const date = dateSections[dateKey].date;
          if (task.type === TaskType.Task) {
            completionData[occurrenceId] = {
              checked: TaskCheckOptions.Checked,
            };
          } else {
            const schedule = getScheduleByDate(task.schedules, {
              date,
              today,
            });
            const target = schedule?.frequency
              ? getScheduleFrequencyDayTarget(schedule)
              : schedule?.completionTarget.count;

            completionData[occurrenceId] = {
              checked: TaskCheckOptions.Checked,
              completionPercentage: getTaskDayCompletionPercentage(task, {
                day: date,
                weekStartsOn,
              }),
              target: target ?? undefined,
            };
          }
        }
      });

      return acc;
    }, dateTreeItems);

    // Sort children within each date section: regular tasks first
    // (by completion time, most recent on top), then habits/repeating.
    sortedDateKeys.forEach((dateKey) => {
      const { completionTimes } = dateSections[dateKey];
      allTreeItems[dateKey].children?.sort((a, b) => {
        const taskIdA = a.split(':').slice(2).join(':');
        const taskIdB = b.split(':').slice(2).join(':');
        const isTaskA = taskTypeMap.get(taskIdA) === TaskType.Task ? 0 : 1;
        const isTaskB = taskTypeMap.get(taskIdB) === TaskType.Task ? 0 : 1;
        if (isTaskA !== isTaskB) {
          return isTaskA - isTaskB;
        }
        const timeA = completionTimes.get(taskIdA)?.getTime() ?? 0;
        const timeB = completionTimes.get(taskIdB)?.getTime() ?? 0;
        return timeB - timeA;
      });
    });

    return {
      tree: {
        [rootId]: {
          id: rootId,
          name: 'Root',
          type: TreeItemTypes.Root,
          children: sortedDateKeys,
        },
        ...allTreeItems,
      },
      completionData,
    };
  }, [tasks, customOrder, sortingMode, today, weekStartsOn, fromDate]);
};
